import React, { useEffect, useState } from "react";
import { Box, Grid2 as Grid } from "@mui/material";
import DeleteIcon from "../../assets/deleteIcon.png";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import DataTable from "../../common/DataTable";
import { defaultUserData } from "../../constants/defaultValues";
import { User } from "../../constants/types";
import { userDetails } from "../../schemas/userDetails";
import ControlledTextField from "../../common/ControlledTextField";
import { api } from "../../api/api";
import { endpoints } from "../../constants/endpoints";
import {
  Title,
  Label,
  PaginationCaption,
  PaginationControlBox,
  ButtonStyle,
  StyledCard,
  NameStyle,
} from "./userManagementStyles";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addUser,
  removeUser,
  setUsersByRole,
} from "../../redux/features/slices/userSlice";
import moment from "moment";
import Toast from "../../common/Toast";
import Pagination from "@mui/material/Pagination";

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const allUsers = useAppSelector((state) => state.users.users);
  const users = allUsers.filter((user) => user.role === "maltora-seller");

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: defaultUserData,
    resolver: yupResolver(userDetails),
    mode: "onSubmit",
  });

  const fieldValues = watch();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const handleGetUsers = async () => {
    setToastMessage("Failed to fetch users");
    setError(true);
    try {
      const response = await api.get(endpoints.getUsers, {
        params: {
          role: "maltora-seller",
          orgId: localStorage.getItem("organizationId"),
        },
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      });

      if (response.data) {
        const transformedData = response.data.data.map((user: any) => ({
          ...user,
          firstName: user.name.split(" ")[0],
          lastName: user.name.split(" ")[1] || "",
          createdDate: moment(user.createdAt).format("DD MMMM YY hh:mm:ss A"),
        }));
        dispatch(
          setUsersByRole({ users: transformedData, role: "maltora-seller" })
        );
      }
      setError(false);
      setToastMessage("Users fetched successfully");
      if (response.status === 200 || response.status === 201) {
        setShowToast(false);
      } else {
        setShowToast(true);
      }
    } catch (error) {
      console.log("Failed to get data, Reason", error);
      setShowToast(true);
    }
  };

  const handleAddUser = async () => {
    setToastMessage("Failed to add user");
    setError(true);

    const values = {
      name: fieldValues.firstName + " " + fieldValues.lastName,
      email: fieldValues.email,
      phone_number: "0",
      country_code: "USA",
      role: "maltora-seller",
    };
    try {
      const response = await api.post(endpoints.addUser, values, {
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      });

      if (response.status === 201) {
        const newUser = {
          id: response.data.data.id,
          firstName: fieldValues.firstName,
          lastName: fieldValues.lastName,
          email: fieldValues.email,
          createdDate: moment(response.data.data.createdAt).format(
            "DD MMMM YY hh:mm:ss A"
          ),
          phone_number: response.data.data.phone_number,
          role: "maltora-seller",
        };
        dispatch(addUser(newUser));
        setError(false);
        setToastMessage("User added successfully");
        reset();
        setShowToast(false);
      } else {
        setShowToast(true);
      }
    } catch (error: any) {
      if (error.response?.data?.error?.message === "User Exists") {
        setToastMessage("The email ID already exists.");
      }
      console.error(error);
      setShowToast(true);
    }
  };

  const handleDeleteUser = async (id: string) => {
    setUserToDelete(id);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    setError(true);
    setToastMessage("Failed to delete user");

    setShowDeleteDialog(false);
    const { email, id } = users.find((user) => user.id === userToDelete) || {};
    try {
      const response = await api.delete(endpoints.deleteUser, {
        data: {
          id: id,
          email: email,
        },
        headers: {
          orgId: localStorage.getItem("organizationId"),
        },
      });
      if (response.status === 200) {
        dispatch(removeUser(id!));
        setError(false);
        setToastMessage("User deleted successfully");
        setShowToast(false);
      } else {
        setShowToast(true);
      }
    } catch (err) {
      console.error("Failed to delete:", err);
      setShowToast(true);
    }
    setUserToDelete(null);
  };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, users.length);
  const totalRows = users.length;

  const paginatedUsers = users.slice(startIndex, endIndex);

  // Columns for the DataTable
  const columns: {
    label: string;
    field: string;
    align?: "left" | "center" | "right";
    renderCell?: (row: User) => JSX.Element;
  }[] = [
    {
      label: "Name",
      field: "name",
      align: "center",
      renderCell: (row: User) => (
        <NameStyle>{`${row.firstName} ${row.lastName}`}</NameStyle>
      ),
    },
    {
      label: "Created Date",
      field: "createdDate",
      align: "center",
    },
    {
      label: "Email",
      field: "email",
      align: "center",
    },
    {
      label: "Delete",
      field: "delete",
      align: "center",
      renderCell: (row: User) => (
        <img
          src={DeleteIcon}
          onClick={() => row.id && handleDeleteUser(row.id)}
          aria-label="Delete"
          style={{ cursor: "pointer" }}
        />
      ),
    },
  ];

  useEffect(() => {
    handleGetUsers();
  }, []);

  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  return (
    <>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box
        display="flex"
        flexDirection={"column"}
        justifyContent="center"
        alignItems="center"
        pt={"10px"}
      >
        <Box width={"100%"}>
          <StyledCard>
            <Box>
              <Title>User Details</Title>

              <Grid container spacing={2} mt={"60px"}>
                {/* Name Label */}
                <Grid size={[12, 4]}>
                  <Label>First Name*</Label>

                  <ControlledTextField
                    control={control}
                    name="firstName"
                    placeholder=""
                  />
                </Grid>

                <Grid size={[12, 4]}>
                  <Label>Last Name*</Label>

                  <ControlledTextField
                    control={control}
                    name="lastName"
                    placeholder=""
                  />
                </Grid>

                {/* Email Label */}
                <Grid size={[12, 4]}>
                  <Label>Email ID*</Label>
                  <ControlledTextField
                    control={control}
                    name="email"
                    placeholder=""
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid size={[0, 6]} />
                <Grid container size={[12, 6]} spacing={"25px"} mt={"40px"}>
                  <Grid size={[12, 6]}>
                    <ButtonStyle variant="outlined" onClick={() => reset()}>
                      Reset
                    </ButtonStyle>
                  </Grid>
                  <Grid size={[12, 6]}>
                    <ButtonStyle
                      variant="contained"
                      onClick={handleSubmit(handleAddUser)}
                    >
                      Add User
                    </ButtonStyle>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Box mt={"80px"} mb={"40px"}>
              <Title>Current Use List</Title>
            </Box>

            {/* Table to display list of users */}
            <Box>
              <DataTable
                columns={columns}
                data={paginatedUsers}
                emptyStateMessage="No users found."
                rowsPerPage={5}
              />
              <PaginationControlBox>
                <PaginationCaption variant="subtitle2">
                  Showing data {startIndex + 1} to {endIndex} of {totalRows}{" "}
                  entries
                </PaginationCaption>
                <Pagination
                  count={Math.ceil(users.length / rowsPerPage)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  shape="rounded"
                  variant="outlined"
                />
              </PaginationControlBox>
            </Box>
          </StyledCard>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        show={showDeleteDialog}
        setShow={setShowDeleteDialog}
        title="Are you sure you want to delete?"
        buttonTitle="Yes, I Am Sure"
        buttonAction={confirmDeleteUser}
        // error={true}
      />
    </>
  );
};

export default UserManagement;
